import React from "react"
import tw from "twin.macro"
import ProfilePhoto, { Width } from "../../profilePhoto"
import { UserSearchResult, Maybe, QuestionSearchResult, AnswerSearchResult } from "../../../graphql/generated/graphql"
import { Routes } from "../../../routes"
import { useTranslation } from "next-i18next"
import { useStopBodyScroll } from "../../../utils/hooks/useStopBodyScroll"

interface DropdownProps {
  emptySearchList: boolean
  emptyPopularList: boolean
  questions: QuestionSearchResult[]
  answers: AnswerSearchResult[]
  users: UserSearchResult[]
  popularUsers: Record<string, any>[]
  focus: number
  setFocus: React.Dispatch<React.SetStateAction<number>>
  handleSelect: (value: Maybe<string> | undefined, route: string, username: string) => void
  input: string
}
export const Dropdown = ({
  emptySearchList,
  emptyPopularList,
  questions,
  answers,
  users,
  popularUsers,
  focus,
  setFocus,
  handleSelect,
  input,
}: DropdownProps) => {
  const { t } = useTranslation("explore")
  useStopBodyScroll(!emptySearchList || !emptyPopularList)

  const searchResult = () => (
    <>
      {!emptySearchList && (
        <div tw="w-full absolute overflow-y-scroll max-h-[416px] z-10 top-[54px] left-0 bg-white tablet-lg:(top-16 border-base-6 border-2 rounded-xl)">
          {users.map(({ name, profilePictureUrl, username, highlight, verified }, index) => (
            <div
              tw="flex items-center h-[52px] border-b border-b-[#E3E3E3] px-3.5 hover:(text-purple cursor-pointer)"
              css={focus === index ? tw`text-white bg-purple` : tw``}
              key={index}
              onClick={() => handleSelect(name, Routes.Profile, username)}
              onMouseEnter={() => setFocus(-1)}
            >
              <ProfilePhoto
                width={Width.W24}
                username={name || ""}
                profilePictureUrl={profilePictureUrl}
                showVerified={verified}
              />
              <div tw="ml-3.5 overflow-hidden">
                <span
                  tw="text-xs"
                  dangerouslySetInnerHTML={{
                    __html: `${highlight.name?.value}`,
                  }}
                ></span>
                <div
                  tw="text-xs whitespace-nowrap overflow-ellipsis overflow-hidden"
                  css={"em {font-weight: bold}"}
                  dangerouslySetInnerHTML={{
                    __html: `${highlight.bio?.value || ""}`,
                  }}
                ></div>
              </div>
            </div>
          ))}
          {answers.map(({ Artist, questionId, highlight }, index) => (
            <div
              tw="flex items-center h-[52px] border-b border-b-[#E3E3E3] px-3.5 hover:(text-purple cursor-pointer)"
              css={focus === index + users.length ? tw`text-white bg-purple` : tw``}
              key={index}
              onClick={() => handleSelect(input, Routes.ShowQuestion, questionId)}
              onMouseEnter={() => setFocus(-1)}
            >
              <ProfilePhoto
                width={Width.W24}
                username={Artist.name || ""}
                profilePictureUrl={Artist.profilePictureUrl}
              />
              <div tw="ml-3.5 overflow-hidden">
                <span tw="text-xs">{t("search.dropdown.answered", { artist: Artist.name })}</span>
                <div
                  tw="text-xs whitespace-nowrap overflow-ellipsis overflow-hidden"
                  css={"em {font-weight: bold}"}
                  dangerouslySetInnerHTML={{
                    __html: `${highlight.transcript?.value || ""}`,
                  }}
                ></div>
              </div>
            </div>
          ))}
          {answers.map(({ Artist, questionId, highlight }, index) => (
            <div
              tw="flex items-center h-[52px] border-b border-b-[#E3E3E3] px-3.5 hover:(text-purple cursor-pointer)"
              css={focus === index + users.length ? tw`text-white bg-purple` : tw``}
              key={index}
              onClick={() => handleSelect(input, Routes.ShowQuestion, questionId)}
              onMouseEnter={() => setFocus(-1)}
            >
              <ProfilePhoto
                width={Width.W24}
                username={Artist.name || ""}
                profilePictureUrl={Artist.profilePictureUrl}
              />
              <div tw="ml-3.5 overflow-hidden">
                <span tw="text-xs">{t("search.dropdown.was_asked", { artist: Artist.name })}</span>
                <div
                  tw="text-xs whitespace-nowrap overflow-ellipsis overflow-hidden"
                  css={"em {font-weight: bold}"}
                  dangerouslySetInnerHTML={{
                    __html: `${highlight.body?.value || ""}`,
                  }}
                ></div>
              </div>
            </div>
          ))}
          {questions.map(({ Artist, id, highlight }, index) => (
            <div
              tw="flex items-center h-[52px] border-b border-b-[#E3E3E3] px-3.5 hover:(text-purple cursor-pointer)"
              css={focus === index + users.length + answers.length ? tw`text-white bg-purple` : tw``}
              key={index}
              onClick={() => handleSelect(input, Routes.ShowQuestion, id)}
              onMouseEnter={() => setFocus(-1)}
            >
              <ProfilePhoto
                width={Width.W24}
                username={Artist.name || ""}
                profilePictureUrl={Artist.profilePictureUrl}
              />
              <div tw="ml-3.5 overflow-hidden">
                <span tw="text-xs">{t("search.dropdown.was_asked", { artist: Artist.name })}</span>
                <div
                  tw="text-xs whitespace-nowrap overflow-ellipsis overflow-hidden"
                  css={"em {font-weight: bold}"}
                  dangerouslySetInnerHTML={{
                    __html: `${highlight.body?.value || ""}`,
                  }}
                ></div>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  )

  const popularList = () => (
    <>
      {!emptyPopularList && (
        <div tw="w-full absolute overflow-y-scroll max-h-[416px] z-10 top-[54px] left-0 bg-white tablet-lg:(top-16 border-base-6 border-2 rounded-xl)">
          <div tw="p-3.5 rounded-full font-bold">{t("search.dropdown.popular_users_label")}</div>
          {popularUsers.map((user, index) => (
            <div
              tw="flex items-center w-full h-[52px] px-3.5 hover:(text-purple cursor-pointer)"
              css={focus === index ? tw`text-white bg-purple` : tw``}
              key={index}
              onClick={() => handleSelect(user.name, Routes.Profile, user.slug)}
              onMouseEnter={() => setFocus(-1)}
            >
              <ProfilePhoto width={Width.W24} username={user.name} profilePictureUrl={user.profilePictureUrl} />
              <div tw="ml-3.5 overflow-hidden">
                <span tw="text-xs">{user.name}</span>
                <div tw="text-xs whitespace-nowrap overflow-ellipsis overflow-hidden">{user.bio}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  )
  const renderDropDown = input ? searchResult() : popularList()
  return <>{renderDropDown}</>
}
